// A fine histogram's x-domain fitted to its data (APS-399): the API's screen histogram is 30
// buckets of 100 ms (0 to 3 s), so loads of 0.3–0.42 s filled one thin bar at the left. The
// fitted domain runs from 0 to about 1.5 × p99, rounded up to a nice step; the buckets past it
// join the overflow bar, so no load is dropped.

export interface Fine {
  bucket_ms: number
  counts: readonly number[]
  overflow: number
}

/** Nice ends for a duration axis, in ms. */
const NICE_MS = [100, 250, 500, 750, 1000, 1500, 2000, 2500, 3000, 4000, 5000, 7500, 10_000]

/** At least this many buckets stay, so a fast screen still reads as a distribution. */
const MIN_BUCKETS = 5

/** The upper edge (ms) of the bucket where the counts reach 99 %, or undefined past the range. */
export function p99Edge(fine: Fine): number | undefined {
  const total = fine.counts.reduce((a, b) => a + b, 0) + fine.overflow
  if (total === 0) return undefined
  let seen = 0
  for (const [i, c] of fine.counts.entries()) {
    seen += c
    if (seen >= total * 0.99) return (i + 1) * fine.bucket_ms
  }
  return undefined
}

/**
 * The histogram cut to 0 … nice(1.5 × p99): the buckets past the end add to the overflow. The
 * same histogram comes back when the data already fills the range or there is none.
 */
export function fitFine(fine: Fine): Fine {
  const p99 = p99Edge(fine)
  if (p99 === undefined) return fine
  const want = 1.5 * p99
  const end = NICE_MS.find((v) => v >= want) ?? want
  const k = Math.max(MIN_BUCKETS, Math.ceil(end / fine.bucket_ms))
  if (k >= fine.counts.length) return fine
  const rest = fine.counts.slice(k).reduce((a, b) => a + b, 0)
  return {
    bucket_ms: fine.bucket_ms,
    counts: fine.counts.slice(0, k),
    overflow: fine.overflow + rest,
  }
}

/** Round steps for a count axis: these times a power of ten, whole numbers only. */
const COUNT_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5]

/**
 * The top of a histogram's count axis (Bars draws 0, top / 2 and top): twice a round, whole
 * step that covers the tallest bar with a little room, so the ticks read 0 / 1,000 / 2,000,
 * never raw fractions of the max such as 854 and 1,708 (Mosaic, APS-399).
 */
export function niceCountTop(max: number): number {
  const want = Math.max(1, (max * 1.1) / 2)
  for (let p = 1; ; p *= 10)
    for (const s of COUNT_STEPS) {
      const step = s * p
      if (Number.isInteger(step) && step >= want) return step * 2
    }
}

/** Two humps in a load histogram: where the dip between them falls, and each side's share. */
export interface TwoHumps {
  /** The upper edge (ms) of the dip's bucket: loads up to here are the fast hump. */
  valleyMs: number
  /** Shares of all loads (0 to 1), the overflow counted with the slower side. */
  under: number
  over: number
}

/**
 * The mock's "Ready under 1.4 s · 66 %" split (Mosaic, APS-392), from the histogram alone: the
 * tallest bucket, a second local peak at least 3 buckets away and at least a fifth as tall, and
 * a real dip between them (at most 0.6 × the lower peak). Undefined for one hump.
 */
export function twoHumps(fine: Fine): TwoHumps | undefined {
  const c = fine.counts
  const total = c.reduce((a, b) => a + b, 0) + fine.overflow
  if (total === 0 || c.length < 5) return undefined
  const first = c.indexOf(Math.max(...c))
  let second = -1
  for (const [i, v] of c.entries()) {
    const local = v >= (c[i - 1] ?? 0) && v >= (c[i + 1] ?? 0) && v > 0
    if (local && Math.abs(i - first) >= 3 && (second < 0 || v > (c[second] ?? 0))) second = i
  }
  if (second < 0) return undefined
  const top = c[first] ?? 0
  const low = c[second] ?? 0
  if (low < top * 0.2) return undefined
  const [a, b] = first < second ? [first, second] : [second, first]
  let valley = a + 1
  for (let i = a + 1; i < b; i++) if ((c[i] ?? 0) < (c[valley] ?? 0)) valley = i
  if ((c[valley] ?? 0) > low * 0.6) return undefined
  const under = c.slice(0, valley + 1).reduce((x, y) => x + y, 0)
  return { valleyMs: (valley + 1) * fine.bucket_ms, under: under / total, over: 1 - under / total }
}
